import { useEffect, useState } from 'react';
import { AlertCircle, RefreshCw } from 'lucide-react';
import type { YoutubePlaylistListItem } from '@news-cms/shared';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { isYouTubeQuotaError, useUpdateYtPlaylist, ytErrorMessage } from '@/lib/youtube-admin';

/** Live preview parser — mirrors `parseYouTubePlaylistUrl` in
 * apps/api/src/lib/youtube-fetch.ts. Returns the resolved playlist id
 * for both raw-id and URL forms; null when input doesn't parse. */
function parsePlaylistInput(raw: string): { kind: 'id' | 'url'; value: string } | null {
  const v = raw.trim();
  if (!v) return null;
  if (/^[A-Za-z0-9_-]{8,128}$/.test(v) && !v.startsWith('http')) {
    return { kind: 'id', value: v };
  }
  try {
    const u = new URL(v);
    const host = u.hostname.replace(/^www\./, '');
    if (!['youtube.com', 'youtube-nocookie.com', 'm.youtube.com'].includes(host)) return null;
    const list = u.searchParams.get('list');
    if (list && /^[A-Za-z0-9_-]{8,128}$/.test(list)) {
      return { kind: 'url', value: list };
    }
  } catch {
    return null;
  }
  return null;
}

/**
 * Edit dialog for a YouTube playlist row. Reused from both the list page
 * (per-card "Edit") and the detail page header "ตั้งค่า" button. Form
 * fields re-seed from `playlist` whenever it changes — keeps the dialog
 * in sync when the parent's TanStack Query refetches after a mutation.
 */
export function PlaylistSettingsDialog({
  playlist,
  open,
  onOpenChange,
}: {
  playlist: YoutubePlaylistListItem;
  open: boolean;
  onOpenChange: (v: boolean) => void;
}) {
  const update = useUpdateYtPlaylist(playlist.id);
  const [name, setName] = useState(playlist.name);
  const [slug, setSlug] = useState(playlist.slug);
  const [description, setDescription] = useState(playlist.description ?? '');
  const [interval, setInterval] = useState(String(playlist.fetchIntervalMinutes));
  const [isActive, setIsActive] = useState(playlist.isActive);
  const [sortOrder, setSortOrder] = useState(String(playlist.sortOrder));
  // Default to the current upstream id (admin sees what it is now and
  // can edit). Empty string means "no change" only if also unchanged.
  const [playlistInput, setPlaylistInput] = useState(playlist.playlistId);
  const [submitError, setSubmitError] = useState<unknown>(null);

  // Re-seed every time the dialog opens OR the underlying playlist row
  // refreshes (e.g. after another tab edited it). Without this, stale
  // form values would silently overwrite the freshly-fetched row.
  useEffect(() => {
    if (!open) return;
    setName(playlist.name);
    setSlug(playlist.slug);
    setDescription(playlist.description ?? '');
    setInterval(String(playlist.fetchIntervalMinutes));
    setIsActive(playlist.isActive);
    setSortOrder(String(playlist.sortOrder));
    setPlaylistInput(playlist.playlistId);
    setSubmitError(null);
  }, [open, playlist]);

  const parsedPreview = parsePlaylistInput(playlistInput);
  const playlistChanged = parsedPreview !== null && parsedPreview.value !== playlist.playlistId;

  const handleSave = () => {
    setSubmitError(null);
    // System playlists rebuild their video list on every refresh, so
    // changing the source (channel id) just changes where future
    // refreshes pull from — no destructive prompt needed. Real-playlist
    // rows DO need the warning since the existing videos belong to the
    // old upstream playlist.
    if (playlistChanged && playlist.videoCount > 0 && !playlist.isSystem) {
      const ok = window.confirm(
        `เปลี่ยน YouTube playlist ID จะลบวิดีโอเก่า ${playlist.videoCount} ตัวออกจาก playlist นี้ ` +
          'เพราะวิดีโอเหล่านั้นมาจาก playlist เดิม — ดำเนินการต่อ?',
      );
      if (!ok) return;
    }
    update.mutate(
      {
        name: name.trim(),
        slug: slug.trim(),
        description: description.trim() || null,
        fetchIntervalMinutes: Math.max(5, Math.min(1440, Number(interval) || 720)),
        isActive,
        sortOrder: Math.max(0, Math.min(9999, Number(sortOrder) || 0)),
        ...(playlistChanged ? { playlistInput: playlistInput.trim() } : {}),
      },
      {
        onSuccess: () => onOpenChange(false),
        onError: (e) => setSubmitError(e),
      },
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{playlist.isSystem ? 'แก้ไข Playlist ระบบ' : 'แก้ไข Playlist'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-4">
          {playlist.isSystem && (
            <div className="rounded-md border border-amber-200 bg-amber-50 p-2.5 text-[11px] leading-relaxed text-amber-900 dark:border-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
              <strong>🌟 Playlist ระบบ</strong> — รายการวิดีโอใน playlist นี้มาจากอัลกอริทึม (top 20
              viewCount ในช่วง 3 วัน ขยาย window อัตโนมัติ 3→7→14→30 วัน). แก้ name/slug/
              description/รอบ poll ได้, แต่ "Playlist ID" ด้านล่างคือ <em>Channel ID</em> ของ
              YouTube. ลบไม่ได้ + วิดีโอข้างในแก้ด้วยมือไม่ได้ (จะถูกเขียนทับทุก refresh).
            </div>
          )}
          <div className="space-y-1.5">
            <Label htmlFor="set-name">ชื่อ</Label>
            <Input
              id="set-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={100}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="set-slug">Slug (ใช้ใน URL ของเว็บ public)</Label>
            <Input
              id="set-slug"
              value={slug}
              onChange={(e) => setSlug(e.target.value)}
              maxLength={80}
              spellCheck={false}
            />
            <p className="text-[11px] text-slate-400">ไทย / a-z / 0-9 และ -</p>
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="set-playlist-input">
              {playlist.isSystem ? 'Channel ID (UC...)' : 'YouTube Playlist (URL หรือ ID)'}
            </Label>
            <Input
              id="set-playlist-input"
              value={playlistInput}
              onChange={(e) => setPlaylistInput(e.target.value)}
              spellCheck={false}
              autoComplete="off"
              placeholder={
                playlist.isSystem
                  ? 'UCygWbILDfBfPN6xR3mrHXHA'
                  : 'https://youtube.com/playlist?list=PL... หรือ PLxxxxxxxx'
              }
            />
            {playlistInput.trim() ? (
              parsedPreview ? (
                <p
                  className={
                    playlistChanged
                      ? 'text-[11px] text-amber-700 dark:text-amber-400'
                      : 'text-[11px] text-emerald-700 dark:text-emerald-400'
                  }
                >
                  {playlistChanged ? '⚠️ จะเปลี่ยนเป็น' : '✓ ปัจจุบัน'}{' '}
                  <span className="font-mono">{parsedPreview.value}</span>
                  {playlistChanged && playlist.videoCount > 0 && !playlist.isSystem && (
                    <span className="ml-1">(จะลบวิดีโอเก่า {playlist.videoCount} ตัว)</span>
                  )}
                </p>
              ) : (
                <p className="text-[11px] text-red-700 dark:text-red-400">
                  ✗ ไม่ใช่{playlist.isSystem ? ' Channel ID' : ' URL หรือ Playlist ID'} ที่ถูกต้อง
                </p>
              )
            ) : null}
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="set-desc">คำอธิบาย</Label>
            <Textarea
              id="set-desc"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              maxLength={500}
              rows={2}
            />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="set-interval">รอบ poll (นาที)</Label>
              <Input
                id="set-interval"
                type="number"
                min={5}
                max={1440}
                value={interval}
                onChange={(e) => setInterval(e.target.value)}
              />
              <p className="text-[11px] text-slate-400">5 - 1440 (default 720)</p>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="set-sort">ลำดับการแสดง</Label>
              <Input
                id="set-sort"
                type="number"
                min={0}
                max={9999}
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
              />
              <p className="text-[11px] text-slate-400">น้อย → แสดงก่อน</p>
            </div>
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
              className="h-4 w-4"
            />
            เปิดใช้งาน (cron poll + แสดงบนเว็บ public)
          </label>

          <div className="rounded-md bg-slate-50 p-2 text-[11px] text-slate-500 dark:bg-slate-800 dark:text-slate-400">
            <div>
              Poll ล่าสุด:{' '}
              {playlist.lastPolledAt
                ? new Date(playlist.lastPolledAt).toLocaleString('th-TH')
                : '—'}
            </div>
            {playlist.lastError &&
              (isYouTubeQuotaError(playlist.lastError) ? (
                // Quota errors aren't a per-playlist config issue — show
                // a calm amber note instead of red. The detailed body
                // stays in the title tooltip if the operator wants it.
                <div className="mt-1 text-amber-700 dark:text-amber-300" title={playlist.lastError}>
                  ⏳ Poll ถูกข้าม: YouTube API quota หมดวันนี้ (reset เที่ยงคืน Pacific = ~14:00
                  เวลาไทย)
                </div>
              ) : (
                <div className="mt-1 text-rose-600 dark:text-rose-400">
                  ข้อผิดพลาดล่าสุด: {playlist.lastError}
                </div>
              ))}
          </div>

          {submitError !== null && (
            <Alert variant="destructive">
              <AlertCircle className="h-4 w-4" />
              <AlertDescription>{ytErrorMessage(submitError)}</AlertDescription>
            </Alert>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={update.isPending}>
            ยกเลิก
          </Button>
          <Button
            onClick={handleSave}
            disabled={update.isPending || !name.trim() || !slug.trim() || parsedPreview === null}
          >
            {update.isPending ? (
              <>
                <RefreshCw className="mr-1.5 h-3.5 w-3.5 animate-spin" /> กำลังบันทึก
              </>
            ) : (
              'บันทึก'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
